import type { LicenceStatus } from '../lib/api'
import { Status, type Tone } from './kit'

// The licence this deployment runs under, in one line, at the top of
// Settings > Platform.
//
// # WHY IT IS HERE
//
// TPRM stops at a licence's expiry, with no grace (release 3), and the
// refusal people meet then is too late to act on: nobody can work. The thirty
// days before it are the only warning there is, so the line turns amber then
// and says what to do. Seats are counted the way the sign-in counts them,
// across every workspace the licence covers.
//
// # WHY THE DATES ARE READ IN UTC
//
// A licence is issued for whole days, and its end is the end of that day in
// UTC. The refusal people meet names the day that way too. Read in India's
// time, a licence ending on 30 September would say 1 October here and 30
// September there.

const DAY: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }

export function licenceDay(iso?: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString('en-IN', DAY)
}

function days(n: number): string {
  return n === 1 ? '1 day' : n + ' days'
}

export type LicenceLineWords = { tone: Tone; label: string; words: string; seats: string; seatTone: Tone }

// licenceLine says, in words, what the API reported. Pure, so the test reads
// exactly what a person reads for each state.
export function licenceLine(s: LicenceStatus): LicenceLineWords {
  const id = s.licenceId ?? 'The licence'
  const until = licenceDay(s.expires)
  let tone: Tone
  let label: string
  let words: string

  if ((s.state === 'VALID' || s.state === 'EXPIRING') && !s.coversThisWorkspace) {
    tone = 'crit'
    label = 'Not covered'
    words = `${id} does not cover this workspace. Ask FOCTTA for a licence that names it.`
  } else {
    switch (s.state) {
      case 'OFF':
        tone = 'neutral'
        label = 'No licence'
        words = 'No licence is installed and none is required, so nothing is enforced. That is for evaluation only; a production install requires one.'
        break
      case 'VALID':
        tone = 'ok'
        label = 'Licence valid'
        words = `${id}, until ${until}` + (s.daysLeft != null ? ` (${days(s.daysLeft)} left).` : '.')
        break
      case 'EXPIRING':
        tone = 'warn'
        label = 'Licence ending'
        words =
          `${id} ends on ${until}` +
          (s.daysLeft != null ? `, in ${days(s.daysLeft)}` : '') +
          '. On that day nobody can work here until a renewed licence is placed, so ask FOCTTA to renew it now.'
        break
      case 'EXPIRED':
        tone = 'crit'
        label = 'Licence expired'
        words = `${id} ended on ${until}. Nothing runs until a renewed licence is placed. Your records are kept.`
        break
      case 'NOT_YET_VALID':
        tone = 'warn'
        label = 'Not started'
        words = `${id} starts on ${licenceDay(s.notBefore)}.`
        break
      case 'MISSING':
        tone = 'crit'
        label = 'No licence'
        words = 'This deployment requires a licence and none is installed. Your records are kept.'
        break
      case 'INVALID':
        tone = 'crit'
        label = 'Not accepted'
        words = 'The licence installed here could not be checked. verify.sh on the server says why.'
        break
      case 'WRONG_HOST':
        tone = 'crit'
        label = 'Wrong address'
        words = `The licence installed here does not cover ${s.host || 'the address this deployment answers at'}.`
        break
      case 'NOT_FOR_TPRM':
        tone = 'crit'
        label = 'Not for TPRM'
        words = 'The licence installed here does not include third-party risk.'
        break
      default:
        tone = 'crit'
        label = 'Unknown'
        words = 'The licence could not be read.'
    }
  }

  let seats = ''
  let seatTone: Tone = 'plain'
  if (s.seatsUsed != null && s.seatsLicensed > 0) {
    seats = `${s.seatsUsed} of ${s.seatsLicensed} seats in use.`
    if (s.seatsUsed >= s.seatsLicensed) {
      seatTone = 'warn'
      seats += ' The next person to sign in is refused until a seat frees up.'
    }
  } else if (s.licenceId && (s.state === 'VALID' || s.state === 'EXPIRING') && s.seatsLicensed === 0) {
    seats = 'No seat limit.'
  }
  return { tone, label, words, seats, seatTone }
}

export function LicenceLine({ status }: { status: LicenceStatus }) {
  const l = licenceLine(status)
  return (
    <p className="sw-row licence-line" aria-label="Licence">
      <Status tone={l.tone}>{l.label}</Status>
      <span>{l.words}</span>
      {l.seats && (l.seatTone === 'warn' ? <Status tone="warn">{l.seats}</Status> : <span className="obs-what">{l.seats}</span>)}
    </p>
  )
}
